import { Box, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

import Facebook from "../../assets/images/icons/catering-rewards/facebook.svg";
import Instagram from "../../assets/images/icons/catering-rewards/instagram.svg";
import Twitter from "../../assets/images/icons/catering-rewards/twitter-new.svg";

import LocationLight from "../../assets/images/icons/catering-rewards/location_light.svg";
import MailLight from "../../assets/images/icons/catering-rewards/mail_light.svg";
import ContactLight from "../../assets/images/icons/catering-rewards/contact_light.svg";

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fafafa",
  borderTop: "1px solid rgba(0,0,0,0.05)",
  padding: theme.spacing(6, 12),
  marginTop: 50,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4, 3),
    marginTop: 30,
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-3px)",
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
  transition: "all 0.3s ease",
  borderRadius: theme.spacing(1),
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.02)",
    transform: "translateX(4px)",
  },
  "& img": {
    width: "20px",
    height: "20px",
    opacity: 0.7,
  },
}));

const SignUpButton = styled("a")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  cursor: "pointer",
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(3),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(215, 90, 46, 0.05)",
    transform: "translateX(4px)",
  },
}));

const Footer = ({
  CateringReward,
  restaurantPageDetails,
  landingData,
  getAddress,
}) => {
  return (
    <FooterContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 4, md: 5 },
        }}
      >
        {/* Left Section - Logo and Social */}
        <Box sx={{ width: { xs: "100%", md: "60%" } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
            }}
          >
            {/* Logo and Social Media */}
            <Box>
              <Box sx={{ mb: 3 }}>
                <img
                  src={CateringReward}
                  alt="catering reward"
                  style={{
                    width: "125px",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </Box>

              {(restaurantPageDetails?.facebookUrl ||
                restaurantPageDetails?.instaUrl ||
                restaurantPageDetails?.xUrl) && (
                <Typography
                  sx={{
                    fontSize: { xs: "16px", md: "20px" },
                    fontWeight: 600,
                    mb: 2,
                    color: "text.primary",
                  }}
                >
                  Stay connected with us
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                {restaurantPageDetails?.facebookUrl && (
                  <SocialButton
                    onClick={() => window.open(restaurantPageDetails?.facebookUrl, "_blank")}
                  >
                    <img src={Facebook} alt="facebook" style={{ width: "40px" }} />
                  </SocialButton>
                )}
                {restaurantPageDetails?.instaUrl && (
                  <SocialButton
                    onClick={() => window.open(restaurantPageDetails?.instaUrl, "_blank")}
                  >
                    <img src={Instagram} alt="instagram" style={{ width: "40px" }} />
                  </SocialButton>
                )}
                {restaurantPageDetails?.xUrl && (
                  <SocialButton onClick={() => window.open(restaurantPageDetails?.xUrl, "_blank")}>
                    <img src={Twitter} alt="twitter" style={{ width: "34px" }} />
                  </SocialButton>
                )}
              </Box>
            </Box>

            {/* Sign Up Section */}
            <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
              <Typography
                sx={{
                  fontSize: { xs: "16px", md: "20px" },
                  fontWeight: 600,
                  mb: { xs: 1, md: 2 },
                  color: "text.primary",
                }}
              >
                Personalised and Exclusive offers
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "12px", md: "14px" },
                  color: "text.secondary",
                  mb: { xs: 2, md: 4 },
                }}
              >
                Start your rewards journey!
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Section - Contact Info */}
        <Box sx={{ width: { xs: "100%", md: "auto" } }}>
          {getAddress() && (
            <ContactItem>
              <img src={LocationLight} alt="location" />
              <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: "text.secondary" }}>
                {getAddress()}
              </Typography>
            </ContactItem>
          )}

          {landingData?.email && (
            <ContactItem>
              <img src={MailLight} alt="email" />
              <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: "text.secondary" }}>
                {landingData?.email}
              </Typography>
            </ContactItem>
          )}

          {landingData?.phoneNumber && (
            <ContactItem>
              <img src={ContactLight} alt="phone" />
              <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: "text.secondary" }}>
                +{landingData?.phoneCountryCode}{" "}
                {landingData?.phoneNumber}
              </Typography>
            </ContactItem>
          )}
        </Box>
      </Box>
    </FooterContainer>
  );
};

export default Footer;

Footer.propTypes = {
  CateringReward: PropTypes.string.isRequired, // URL or component
  restaurantPageDetails: PropTypes.shape({
    facebookUrl: PropTypes.string,
    instaUrl: PropTypes.string,
    xUrl: PropTypes.string,
  }),
  landingData: PropTypes.shape({
    email: PropTypes.string,
    phoneCountryCode: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  getAddress: PropTypes.func.isRequired,
};

Footer.defaultProps = {
  restaurantPageDetails: {
    facebookUrl: null,
    instaUrl: null,
    xUrl: null,
  },
  landingData: {
    email: "",
    phoneCountryCode: "",
    phoneNumber: "",
  },
};
